import dayjs from 'dayjs'
import {
  ICustomerTransaction,
  ISupplierTransaction,
  ITransactionFilter,
  ITransactionsReport,
  TRANSACTION_TYPE
} from 'state/transaction/transaction.types'
import { ENTITY_TYPE } from 'shared/constants'

type TxnRow = (ICustomerTransaction | ISupplierTransaction) & {
  outStandingAmount?: number
  /** Per-row account name — rendered in a dedicated column when showAccountColumn. */
  accountName?: string
  /** Per-row account mobile — rendered under the name (Android statement parity). */
  accountMobile?: string
}

export interface IDownloadTransactionPdfArgs {
  entityType: ENTITY_TYPE
  entityName: string
  entityMobile?: string
  currentBalance: number
  business: {
    name: string
    mobile: string
    address: string
  }
  transactions: TxnRow[]
  filter: ITransactionFilter
  transactionsReport: ITransactionsReport
  /**
   * Consolidated (all-accounts) reports set this to render a dedicated Account column
   * (name) separate from Notes, matching the individual report's separate-notes style.
   * Single-account reports leave it off (name lives in the header).
   */
  showAccountColumn?: boolean
}

// A4 dimensions in CSS pixels at 96 DPI: 794 x 1123
// We render at 2× scale for a sharp image embedded in the PDF.
const PAGE_W = 794
const PAGE_H = 1123
const SCALE = 2

const PAGE_MARGIN = 30
const BLOCK_PADDING = 20

const COLOR_TEXT = '#212121'
const COLOR_MUTED = '#757575'
const COLOR_BORDER = '#E0E0E0'
const COLOR_HEADER_BG = '#F5F5F5'
const COLOR_GREEN = '#388E3C'
const COLOR_RED = '#D32F2F'
const COLOR_WHITE = '#FFFFFF'

const FONT_STACK = '"Helvetica Neue", "Segoe UI", Roboto, "DejaVu Sans", Arial, sans-serif'

const indianFmt = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 })
const formatAmount = (n: number) => indianFmt.format(Math.abs(Number(n) || 0))
const formatRupee = (n: number) => `₹${formatAmount(n)}`
const formatDate = (timestamp: number) => dayjs.unix(timestamp).format('DD MMM YYYY')

// ─── Canvas helpers ────────────────────────────────────────────────────────────

const createPageCanvas = (): HTMLCanvasElement => {
  const canvas = document.createElement('canvas')
  canvas.width = PAGE_W * SCALE
  canvas.height = PAGE_H * SCALE
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Canvas 2D context not available')
  }
  ctx.scale(SCALE, SCALE)
  ctx.textBaseline = 'top'
  ctx.fillStyle = COLOR_WHITE
  ctx.fillRect(0, 0, PAGE_W, PAGE_H)
  return canvas
}

const setFont = (
  ctx: CanvasRenderingContext2D,
  size: number,
  bold = false,
  color: string = COLOR_TEXT
) => {
  ctx.font = `${bold ? 'bold ' : ''}${size}px ${FONT_STACK}`
  ctx.fillStyle = color
}

const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
  if (!text) {
    return []
  }
  const words = String(text).split(/\s+/)
  const lines: string[] = []
  let current = ''
  for (const word of words) {
    const test = current ? `${current} ${word}` : word
    if (ctx.measureText(test).width <= maxWidth) {
      current = test
    } else {
      if (current) {
        lines.push(current)
      }
      // word itself longer than maxWidth — break by char
      if (ctx.measureText(word).width > maxWidth) {
        let chunk = ''
        for (const ch of word) {
          const t = chunk + ch
          if (ctx.measureText(t).width > maxWidth) {
            if (chunk) {
              lines.push(chunk)
            }
            chunk = ch
          } else {
            chunk = t
          }
        }
        current = chunk
      } else {
        current = word
      }
    }
  }
  if (current) {
    lines.push(current)
  }
  return lines
}

// ─── Drawing primitives mirroring the Android KMP template layout ──────────────

interface ReportData {
  business: { name: string; mobile: string; address: string }
  accountLabel: string
  entityName: string
  entityMobile: string
  startDateStr: string
  endDateStr: string
  periodDelta: number
  currentBalance: number
  paymentCount: number
  creditCount: number
  totalPayment: number
  totalCredit: number
  totalDiscount: number
  /** Centered statement title, e.g. "CUSTOMER ACCOUNT STATEMENT" (account-column layout). */
  reportTitle: string
  /** Name-column header, e.g. "CUSTOMER NAME" (account-column layout). */
  nameColumnHeader: string
  showAccountColumn: boolean
}

const HEADER_LINE_H = 18

const drawHeader = (ctx: CanvasRenderingContext2D, data: ReportData): number => {
  const x = PAGE_MARGIN
  const width = PAGE_W - 2 * PAGE_MARGIN
  const innerW = width - 2 * BLOCK_PADDING
  const leftW = innerW * 0.6 - 12
  const startY = PAGE_MARGIN

  // measure heights with the right fonts
  setFont(ctx, 13)
  const addressLines = data.business.address ? wrapText(ctx, data.business.address, leftW) : []

  let leftH = 18 + 6 // business name (18px) + gap
  leftH += 13 + 4 // mobile
  leftH += addressLines.length * HEADER_LINE_H

  let rightH = 13 // created on
  if (!data.showAccountColumn) {
    rightH += 12 + 13 + 4 + 16 + 6 + 13 // label + entity name + mobile
  }

  const blockH = Math.max(leftH, rightH) + 2 * BLOCK_PADDING

  // background
  ctx.fillStyle = COLOR_HEADER_BG
  ctx.fillRect(x, startY, width, blockH)

  // left column
  let lY = startY + BLOCK_PADDING
  setFont(ctx, 18, true)
  ctx.textAlign = 'left'
  ctx.fillText(data.business.name || ' ', x + BLOCK_PADDING, lY)
  lY += 18 + 6

  setFont(ctx, 13)
  ctx.fillText(`Mobile: ${data.business.mobile || '-'}`, x + BLOCK_PADDING, lY)
  lY += 13 + 4

  if (addressLines.length > 0) {
    for (const line of addressLines) {
      ctx.fillText(line, x + BLOCK_PADDING, lY)
      lY += HEADER_LINE_H
    }
  }

  // right column (right-aligned)
  const rightEdge = x + width - BLOCK_PADDING
  let rY = startY + BLOCK_PADDING
  ctx.textAlign = 'right'

  setFont(ctx, 13)
  ctx.fillText(`Created on: ${data.endDateStr}`, rightEdge, rY)

  // Single-account statements show the account identity on the right; the
  // consolidated (account-column) statement uses a centered title instead.
  if (!data.showAccountColumn) {
    rY += 13 + 12
    ctx.fillText(`${data.accountLabel}:`, rightEdge, rY)
    rY += 13 + 4
    setFont(ctx, 16, true)
    ctx.fillText(data.entityName || '-', rightEdge, rY)
    rY += 16 + 6
    setFont(ctx, 13)
    ctx.fillText(`Mobile: ${data.entityMobile || '-'}`, rightEdge, rY)
  }

  ctx.textAlign = 'left'
  return startY + blockH + 30
}

const drawBalanceSection = (ctx: CanvasRenderingContext2D, y: number, data: ReportData): number => {
  const centerX = PAGE_W / 2

  // Consolidated (account-column) statement: mirrors the Android layout —
  // centered title, date range, "NET BALANCE" + DUE/ADVANCE, then CREDITS /
  // PAYMENTS summary blocks side by side.
  if (data.showAccountColumn) {
    let nextY = y
    ctx.textAlign = 'center'

    setFont(ctx, 18, true, COLOR_TEXT)
    ctx.fillText(data.reportTitle, centerX, nextY)
    nextY += 18 + 8

    setFont(ctx, 13, false, COLOR_MUTED)
    ctx.fillText(`${data.startDateStr} - ${data.endDateStr}`, centerX, nextY)
    nextY += 13 + 16

    setFont(ctx, 12, false, COLOR_MUTED)
    ctx.fillText('NET BALANCE', centerX, nextY)
    nextY += 12 + 8

    const net = data.periodDelta // credits − payments − discount (Android canonical net)
    const netColor = net >= 0 ? COLOR_RED : COLOR_GREEN
    const netSuffix = net >= 0 ? ' DUE' : ' ADVANCE'
    setFont(ctx, 22, true, netColor)
    ctx.fillText(`${formatRupee(net)}${netSuffix}`, centerX, nextY)
    nextY += 22 + 20

    const leftCenter = PAGE_W / 4
    const rightCenter = (PAGE_W * 3) / 4
    setFont(ctx, 13, false, COLOR_MUTED)
    ctx.fillText(`${data.creditCount} CREDITS`, leftCenter, nextY)
    ctx.fillText(`${data.paymentCount} PAYMENTS`, rightCenter, nextY)
    nextY += 13 + 6
    setFont(ctx, 14, true, COLOR_RED)
    ctx.fillText(formatRupee(data.totalCredit), leftCenter, nextY)
    setFont(ctx, 14, true, COLOR_GREEN)
    ctx.fillText(formatRupee(data.totalPayment), rightCenter, nextY)
    nextY += 14 + 20

    // Discounts offered — shown below the summary blocks (Android parity).
    if (data.totalDiscount > 0) {
      setFont(ctx, 13, false, COLOR_MUTED)
      ctx.textAlign = 'center'
      ctx.fillText(`Discounts offered: ${formatRupee(data.totalDiscount)}`, centerX, nextY)
      nextY += 13 + 14
    }

    ctx.textAlign = 'left'
    return nextY
  }

  // Single-account statement (unchanged): big net delta + balance-range label.
  const color = data.periodDelta < 0 ? COLOR_GREEN : COLOR_RED
  setFont(ctx, 32, true, color)
  ctx.textAlign = 'center'
  ctx.fillText(formatRupee(data.periodDelta), centerX, y)
  let nextY = y + 32 + 8

  setFont(ctx, 14, false, COLOR_TEXT)
  ctx.fillText(`Balance | ${data.startDateStr} - ${data.endDateStr}`, centerX, nextY)
  nextY += 14 + 22

  ctx.textAlign = 'left'
  return nextY
}

// Column geometry.
//   default (single account): Date 20% | Notes 40% | Payment 20% | Credit 20%
//   with account column     : Date 14% | Account 20% | Notes 26% | Payment 20% | Credit 20%
const tableGeometry = (showAccount = false) => {
  const tableX = PAGE_MARGIN
  const tableW = PAGE_W - 2 * PAGE_MARGIN
  if (showAccount) {
    const colDate = tableW * 0.14
    const colAccount = tableW * 0.2
    const colNotes = tableW * 0.26
    const colPay = tableW * 0.2
    const colCredit = tableW * 0.2
    const colAccountStart = tableX + colDate
    const colNotesStart = colAccountStart + colAccount
    // Android order: Date | Name | Notes | Credit | Payment (credit before payment).
    const colCreditStart = colNotesStart + colNotes
    return {
      tableX,
      tableW,
      showAccount: true,
      colDateStart: tableX,
      colAccountStart,
      colNotesStart,
      colCreditStart,
      colPayStart: colCreditStart + colCredit,
      colDate,
      colAccount,
      colNotes,
      colPay,
      colCredit
    }
  }
  const colDate = tableW * 0.2
  const colNotes = tableW * 0.4
  const colPay = tableW * 0.2
  const colCredit = tableW * 0.2
  return {
    tableX,
    tableW,
    showAccount: false,
    colDateStart: tableX,
    colAccountStart: tableX + colDate, // unused when !showAccount
    colNotesStart: tableX + colDate,
    colPayStart: tableX + colDate + colNotes,
    colCreditStart: tableX + colDate + colNotes + colPay,
    colDate,
    colAccount: 0,
    colNotes,
    colPay,
    colCredit
  }
}

const drawTableHeader = (ctx: CanvasRenderingContext2D, y: number, data: ReportData): number => {
  const g = tableGeometry(data.showAccountColumn)

  // Android-style header (consolidated): single row, uppercase labels, the
  // counts/totals live in the summary section rather than the header.
  if (g.showAccount) {
    const headerH = 34
    ctx.fillStyle = COLOR_HEADER_BG
    ctx.fillRect(g.tableX, y, g.tableW, headerH)
    const ty = y + 12
    setFont(ctx, 13, true, COLOR_TEXT)
    ctx.textAlign = 'left'
    ctx.fillText('DATE', g.colDateStart + 8, ty)
    ctx.fillText(data.nameColumnHeader, g.colAccountStart + 8, ty)
    ctx.fillText('NOTES', g.colNotesStart + 8, ty)
    ctx.textAlign = 'center'
    setFont(ctx, 13, true, COLOR_RED)
    ctx.fillText('CREDIT', g.colCreditStart + g.colCredit / 2, ty)
    setFont(ctx, 13, true, COLOR_GREEN)
    ctx.fillText('PAYMENT', g.colPayStart + g.colPay / 2, ty)
    ctx.textAlign = 'left'
    return y + headerH
  }

  const headerH = 56
  ctx.fillStyle = COLOR_HEADER_BG
  ctx.fillRect(g.tableX, y, g.tableW, headerH)

  // Date / Notes labels (left-aligned)
  setFont(ctx, 14, true, COLOR_TEXT)
  ctx.textAlign = 'left'
  ctx.fillText('Date', g.colDateStart + 8, y + 12)
  ctx.fillText('Notes', g.colNotesStart + 8, y + 12)

  // Payment count + total (green, right-aligned, two lines)
  ctx.textAlign = 'right'
  setFont(ctx, 14, true, COLOR_GREEN)
  ctx.fillText(`Payment(${data.paymentCount})`, g.colPayStart + g.colPay - 8, y + 8)
  setFont(ctx, 13, false, COLOR_GREEN)
  ctx.fillText(formatRupee(data.totalPayment), g.colPayStart + g.colPay - 8, y + 28)

  // Credit count + total (red, right-aligned, two lines)
  setFont(ctx, 14, true, COLOR_RED)
  ctx.fillText(`Credit(${data.creditCount})`, g.colCreditStart + g.colCredit - 8, y + 8)
  setFont(ctx, 13, false, COLOR_RED)
  ctx.fillText(formatRupee(data.totalCredit), g.colCreditStart + g.colCredit - 8, y + 28)

  ctx.textAlign = 'left'
  return y + headerH
}

const ROW_LINE_H = 18
const ROW_PAD_TOP = 14
const ROW_PAD_BOT = 14

const calculateRowHeight = (
  ctx: CanvasRenderingContext2D,
  txn: TxnRow,
  showAccount = false
): number => {
  const g = tableGeometry(showAccount)
  setFont(ctx, 14)
  const noteLines = txn.note ? wrapText(ctx, String(txn.note), g.colNotes - 16) : []
  const nameLines =
    showAccount && txn.accountName ? wrapText(ctx, String(txn.accountName), g.colAccount - 16) : []
  // The account cell shows the mobile on an extra line under the name.
  const nameTotalLines = nameLines.length + (showAccount && txn.accountMobile ? 1 : 0)
  const lines = Math.max(1, noteLines.length, nameTotalLines)
  return ROW_PAD_TOP + lines * ROW_LINE_H + ROW_PAD_BOT
}

const drawRow = (
  ctx: CanvasRenderingContext2D,
  y: number,
  txn: TxnRow,
  showAccount = false
): number => {
  const g = tableGeometry(showAccount)
  const rowH = calculateRowHeight(ctx, txn, showAccount)
  const isPayment = txn.transactionType === TRANSACTION_TYPE.PAYMENT
  const isCredit = txn.transactionType === TRANSACTION_TYPE.CREDIT
  const amount = Number(txn.amount || 0)
  const textY = y + ROW_PAD_TOP

  // date
  setFont(ctx, 14, false, COLOR_TEXT)
  ctx.textAlign = 'left'
  ctx.fillText(formatDate(Number(txn.billedTime)), g.colDateStart + 8, textY)

  // account name (wrapped) + mobile — consolidated report only
  if (showAccount && txn.accountName) {
    setFont(ctx, 14, false, COLOR_TEXT)
    ctx.textAlign = 'left'
    let aY = textY
    for (const line of wrapText(ctx, String(txn.accountName), g.colAccount - 16)) {
      ctx.fillText(line, g.colAccountStart + 8, aY)
      aY += ROW_LINE_H
    }
    if (txn.accountMobile) {
      setFont(ctx, 13, false, COLOR_MUTED)
      ctx.fillText(String(txn.accountMobile), g.colAccountStart + 8, aY)
    }
  }

  // notes (wrapped)
  if (txn.note) {
    const noteLines = wrapText(ctx, String(txn.note), g.colNotes - 16)
    let nY = textY
    for (const line of noteLines) {
      ctx.fillText(line, g.colNotesStart + 8, nY)
      nY += ROW_LINE_H
    }
  }

  // payment / credit — centered in the account layout (Android), right-aligned otherwise
  if (isPayment) {
    setFont(ctx, 14, false, COLOR_GREEN)
    if (showAccount) {
      ctx.textAlign = 'center'
      ctx.fillText(formatRupee(amount), g.colPayStart + g.colPay / 2, textY)
    } else {
      ctx.textAlign = 'right'
      ctx.fillText(formatRupee(amount), g.colPayStart + g.colPay - 8, textY)
    }
  }

  if (isCredit) {
    setFont(ctx, 14, false, COLOR_RED)
    if (showAccount) {
      ctx.textAlign = 'center'
      ctx.fillText(formatRupee(amount), g.colCreditStart + g.colCredit / 2, textY)
    } else {
      ctx.textAlign = 'right'
      ctx.fillText(formatRupee(amount), g.colCreditStart + g.colCredit - 8, textY)
    }
  }

  // bottom border
  ctx.strokeStyle = COLOR_BORDER
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(g.tableX, y + rowH)
  ctx.lineTo(g.tableX + g.tableW, y + rowH)
  ctx.stroke()

  ctx.textAlign = 'left'
  return y + rowH
}

const drawEmpty = (ctx: CanvasRenderingContext2D, y: number): number => {
  setFont(ctx, 14, false, COLOR_MUTED)
  ctx.textAlign = 'center'
  ctx.fillText('No transactions were recorded during the selected period', PAGE_W / 2, y + 30)
  ctx.textAlign = 'left'
  return y + 80
}

const drawFooter = (ctx: CanvasRenderingContext2D, y: number, data: ReportData): number => {
  const x = PAGE_MARGIN
  const w = PAGE_W - 2 * PAGE_MARGIN
  const padding = 18
  const footerH = padding * 2 + 14 + 8 + 13 + 12 + 13

  ctx.fillStyle = COLOR_HEADER_BG
  ctx.fillRect(x, y, w, footerH)

  const rightEdge = x + w - padding
  const balanceColor =
    data.currentBalance > 0 ? COLOR_GREEN : data.currentBalance < 0 ? COLOR_RED : COLOR_TEXT
  const suffix =
    data.currentBalance > 0
      ? ' (Total Balance Advance)'
      : data.currentBalance < 0
      ? ' (Total Balance Due)'
      : ''

  // Single right-aligned line: "Current Balance: " + amount + suffix
  // Build right-to-left so right edges align.
  ctx.textAlign = 'right'
  let cursorX = rightEdge

  if (suffix) {
    setFont(ctx, 13, false, COLOR_TEXT)
    ctx.fillText(suffix, cursorX, y + padding)
    cursorX -= ctx.measureText(suffix).width
  }

  setFont(ctx, 14, true, balanceColor)
  const amountStr = formatRupee(data.currentBalance)
  ctx.fillText(amountStr, cursorX, y + padding)
  cursorX -= ctx.measureText(amountStr).width + 6

  setFont(ctx, 14, false, COLOR_TEXT)
  ctx.fillText('Current Balance:', cursorX, y + padding)

  // ( As of DATE )
  setFont(ctx, 13, false, COLOR_TEXT)
  ctx.textAlign = 'right'
  ctx.fillText(`( As of ${data.endDateStr} )`, rightEdge, y + padding + 14 + 8)

  // Powered by OkCredit
  ctx.fillText('Powered by OkCredit', rightEdge, y + padding + 14 + 8 + 13 + 12)

  ctx.textAlign = 'left'
  return y + footerH
}

// ─── Render report into canvases (one per page) ────────────────────────────────

const renderPages = (args: IDownloadTransactionPdfArgs): HTMLCanvasElement[] => {
  const isSupplier = args.entityType === ENTITY_TYPE.SUPPLIER
  const accountLabel = isSupplier ? 'Supplier' : 'Customer'

  const sortedTxns = [...args.transactions].sort((a, b) => {
    const billDiff = (b.billedTime || 0) - (a.billedTime || 0)
    if (billDiff !== 0) {
      return billDiff
    }
    return (b.createdTime || 0) - (a.createdTime || 0)
  })

  const todayTs = dayjs().unix()
  let startTs: number
  let endTs: number
  if (args.filter.label === 'Overall') {
    const oldest = sortedTxns[sortedTxns.length - 1]
    startTs = oldest?.billedTime ? Number(oldest.billedTime) : todayTs
    endTs = args.filter.endTime || todayTs
  } else if (args.filter.label === 'Today') {
    startTs = args.filter.startTime || todayTs
    endTs = args.filter.startTime || todayTs
  } else {
    startTs = args.filter.startTime || todayTs
    endTs = args.filter.endTime || todayTs
  }

  const data: ReportData = {
    business: args.business,
    accountLabel,
    entityName: args.entityName,
    entityMobile: args.entityMobile || '',
    startDateStr: formatDate(startTs),
    endDateStr: formatDate(endTs),
    periodDelta:
      (args.transactionsReport.credit || 0) -
      (args.transactionsReport.payment || 0) -
      (args.transactionsReport.discount || 0),
    currentBalance: args.currentBalance,
    paymentCount: args.transactionsReport.paymentCount || 0,
    creditCount: args.transactionsReport.creditCount || 0,
    totalPayment: args.transactionsReport.payment || 0,
    totalCredit: args.transactionsReport.credit || 0,
    totalDiscount: args.transactionsReport.discount || 0,
    reportTitle: isSupplier ? 'SUPPLIER ACCOUNT STATEMENT' : 'CUSTOMER ACCOUNT STATEMENT',
    nameColumnHeader: isSupplier ? 'SUPPLIER NAME' : 'CUSTOMER NAME',
    showAccountColumn: !!args.showAccountColumn
  }

  const pages: HTMLCanvasElement[] = []
  const footerReservedH = 100 // height reserved at bottom so footer fits

  let canvas = createPageCanvas()
  let ctx = canvas.getContext('2d')!
  pages.push(canvas)

  // First page: header + balance + table header + first batch of rows
  let y = drawHeader(ctx, data)
  y = drawBalanceSection(ctx, y, data)
  y = drawTableHeader(ctx, y, data)

  const bottomLimit = PAGE_H - PAGE_MARGIN

  if (sortedTxns.length === 0) {
    y = drawEmpty(ctx, y)
  } else {
    for (let i = 0; i < sortedTxns.length; i++) {
      const txn = sortedTxns[i]
      const rowH = calculateRowHeight(ctx, txn, data.showAccountColumn)
      const isLastTxn = i === sortedTxns.length - 1
      const remaining = bottomLimit - y
      const needed = rowH + (isLastTxn ? footerReservedH : 0)

      if (remaining < needed) {
        // start a new page
        canvas = createPageCanvas()
        ctx = canvas.getContext('2d')!
        pages.push(canvas)
        y = PAGE_MARGIN
        y = drawTableHeader(ctx, y, data)
      }

      y = drawRow(ctx, y, txn, data.showAccountColumn)
    }
  }

  // Footer: try to fit on current page, else push to new page
  const footerEstH = 100
  if (PAGE_H - PAGE_MARGIN - y < footerEstH) {
    canvas = createPageCanvas()
    ctx = canvas.getContext('2d')!
    pages.push(canvas)
    y = PAGE_MARGIN
  }
  y += 20
  drawFooter(ctx, y, data)

  return pages
}

// ─── Canvas → JPEG bytes ───────────────────────────────────────────────────────

const canvasToJpegBytes = (canvas: HTMLCanvasElement): Uint8Array => {
  const dataUrl = canvas.toDataURL('image/jpeg', 0.92)
  const base64 = dataUrl.substring(dataUrl.indexOf(',') + 1)
  const bin = atob(base64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) {
    bytes[i] = bin.charCodeAt(i)
  }
  return bytes
}

// ─── Hand-rolled minimal PDF assembly ──────────────────────────────────────────

const buildPdfBytes = (jpegs: Uint8Array[]): Uint8Array => {
  const enc = new TextEncoder()
  const chunks: Uint8Array[] = []
  let totalLen = 0
  const writeText = (s: string) => {
    const b = enc.encode(s)
    chunks.push(b)
    totalLen += b.length
  }
  const writeBytes = (b: Uint8Array) => {
    chunks.push(b)
    totalLen += b.length
  }
  const currentOffset = () => totalLen

  const N = jpegs.length
  const objectOffsets: number[] = [] // index 0 -> obj 1, etc.

  // %PDF header + binary marker (so file is recognised as binary by tools)
  writeText('%PDF-1.4\n')
  writeBytes(new Uint8Array([0x25, 0xc4, 0xc5, 0xc6, 0xc7, 0x0a]))

  // obj 1: catalog
  objectOffsets.push(currentOffset())
  writeText('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n')

  // obj 2: pages root
  objectOffsets.push(currentOffset())
  const kids: string[] = []
  for (let i = 0; i < N; i++) {
    kids.push(`${3 + 3 * i} 0 R`)
  }
  writeText(`2 0 obj\n<< /Type /Pages /Kids [${kids.join(' ')}] /Count ${N} >>\nendobj\n`)

  // per-page objects
  const imgW = PAGE_W * SCALE
  const imgH = PAGE_H * SCALE

  for (let i = 0; i < N; i++) {
    const pageObjNum = 3 + 3 * i
    const imgObjNum = 4 + 3 * i
    const contentObjNum = 5 + 3 * i

    // page dict
    objectOffsets.push(currentOffset())
    writeText(
      `${pageObjNum} 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /XObject << /Im0 ${imgObjNum} 0 R >> >> /Contents ${contentObjNum} 0 R >>\nendobj\n`
    )

    // image XObject
    const jpeg = jpegs[i]
    objectOffsets.push(currentOffset())
    writeText(
      `${imgObjNum} 0 obj\n<< /Type /XObject /Subtype /Image /Width ${imgW} /Height ${imgH} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`
    )
    writeBytes(jpeg)
    writeText('\nendstream\nendobj\n')

    // content stream — paint the image scaled to fill the A4 page (595×842 pts)
    const cs = 'q 595 0 0 842 0 0 cm /Im0 Do Q\n'
    const csBytes = enc.encode(cs)
    objectOffsets.push(currentOffset())
    writeText(
      `${contentObjNum} 0 obj\n<< /Length ${csBytes.length} >>\nstream\n${cs}endstream\nendobj\n`
    )
  }

  // xref
  const xrefOffset = currentOffset()
  const totalObjs = 2 + 3 * N
  writeText(`xref\n0 ${totalObjs + 1}\n`)
  writeText('0000000000 65535 f \n')
  for (const off of objectOffsets) {
    writeText(`${off.toString().padStart(10, '0')} 00000 n \n`)
  }

  // trailer
  writeText(`trailer\n<< /Size ${totalObjs + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`)

  // concat
  const out = new Uint8Array(totalLen)
  let pos = 0
  for (const c of chunks) {
    out.set(c, pos)
    pos += c.length
  }
  return out
}

// ─── Public entry ──────────────────────────────────────────────────────────────

const generate = (args: IDownloadTransactionPdfArgs): { bytes: Uint8Array; fileName: string } => {
  const isSupplier = args.entityType === ENTITY_TYPE.SUPPLIER
  const docTypeName = isSupplier ? 'SupplierStatement' : 'CustomerStatement'

  const sortedTxns = [...args.transactions].sort((a, b) => {
    const billDiff = (b.billedTime || 0) - (a.billedTime || 0)
    if (billDiff !== 0) {
      return billDiff
    }
    return (b.createdTime || 0) - (a.createdTime || 0)
  })

  const todayTs = dayjs().unix()
  let startTs: number
  let endTs: number
  if (args.filter.label === 'Overall') {
    const oldest = sortedTxns[sortedTxns.length - 1]
    startTs = oldest?.billedTime ? Number(oldest.billedTime) : todayTs
    endTs = args.filter.endTime || todayTs
  } else if (args.filter.label === 'Today') {
    startTs = args.filter.startTime || todayTs
    endTs = args.filter.startTime || todayTs
  } else {
    startTs = args.filter.startTime || todayTs
    endTs = args.filter.endTime || todayTs
  }

  const fileName = `OkCredit_${docTypeName}_${formatDate(startTs)}_${formatDate(endTs)}.pdf`
  const pages = renderPages(args)
  const jpegs = pages.map(canvasToJpegBytes)
  const bytes = buildPdfBytes(jpegs)
  return { bytes, fileName }
}

const triggerDownload = (bytes: Uint8Array, fileName: string) => {
  // Copy into a fresh ArrayBuffer-backed Uint8Array so Blob doesn't
  // share underlying buffer with our working array.
  const copy = new Uint8Array(bytes)
  const blob = new Blob([copy], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export const downloadTransactionReportPdf = (args: IDownloadTransactionPdfArgs): void => {
  const { bytes, fileName } = generate(args)
  triggerDownload(bytes, fileName)
}

export const printTransactionReportPdf = (args: IDownloadTransactionPdfArgs): void => {
  const { bytes } = generate(args)
  const copy = new Uint8Array(bytes)
  const blob = new Blob([copy], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)

  // Load the PDF in a hidden iframe and trigger print directly.
  // Works in Chrome/Edge; in Firefox the built-in PDF viewer may not honour
  // print() — in that case the iframe just sits there silently. Fallback:
  // user can still re-trigger from the Download menu.
  const iframe = document.createElement('iframe')
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  iframe.setAttribute('aria-hidden', 'true')
  iframe.src = url

  iframe.onload = () => {
    // Small delay lets Chrome's PDF viewer finish initialising before print().
    setTimeout(() => {
      try {
        iframe.contentWindow?.focus()
        iframe.contentWindow?.print()
      } catch {
        // ignore — user can still print from the visible viewer
      }
    }, 150)
  }

  document.body.appendChild(iframe)

  // Keep the iframe around long enough for the print dialog to use it,
  // then clean up.
  setTimeout(() => {
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe)
    }
    URL.revokeObjectURL(url)
  }, 60000)
}
